import React, { useEffect, useState } from 'react';
import { sessionsAPI } from '../../api/sessions';
import type { SurveySession, SessionStats } from '../../api/sessions';

interface SessionHistoryProps {
  surveyId: string;
  onResume?: (sessionId: string) => void;
  onStartNew?: () => void;
}

export const SessionHistory: React.FC<SessionHistoryProps> = ({ 
  surveyId, 
  onResume,
  onStartNew 
}) => {
  const [sessions, setSessions] = useState<SurveySession[]>([]);
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [canAttempt, setCanAttempt] = useState(true);
  const [loading, setLoading] = useState(true);
  const [_activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    loadSessionData();
  }, [surveyId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      
      // Load sessions and stats in parallel
      const [sessionsData, statsData, canAttemptData] = await Promise.all([
        sessionsAPI.getSurveySessions(surveyId),
        sessionsAPI.getSessionStats(surveyId),
        sessionsAPI.checkCanAttempt(surveyId)
      ]);
      
      setSessions(sessionsData);
      setStats(statsData);
      setCanAttempt(canAttemptData.can_attempt);
      setActiveSessionId(canAttemptData.active_session_id || null);
    } catch (err) {
      console.error('Failed to load session data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="text-foreground-muted">Loading session history...</div>;
  }

  const activeSession = sessions.find(s => s.status === 'in_progress');

  return (
    <div className="space-y-6">
      {/* Current Status */}
      {activeSession && (
        <div className="bg-primary/10 border border-primary-light rounded-lg p-4">
          <h3 className="font-semibold text-primary-dark mb-2">Resume Your Session</h3>
          <p className="text-primary-dark mb-3">
            You have an incomplete session ({Math.round(activeSession.progress_percentage)}% complete)
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => onResume && onResume(activeSession.id)}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Resume Survey
            </button>
            <button
              onClick={async () => {
                if (confirm('Are you sure you want to abandon this session and start fresh?')) {
                  await sessionsAPI.abandonSession(activeSession.id);
                  loadSessionData();
                }
              }}
              className="px-4 py-2 bg-border text-foreground rounded-md hover:bg-foreground-muted"
            >
              Start Fresh
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      {stats && stats.total_attempts > 0 && (
        <div className="bg-background rounded-lg p-4">
          <h3 className="font-semibold mb-3">Your Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-2xl font-bold text-foreground">{stats.total_attempts}</div>
              <div className="text-sm text-foreground-muted">Total Attempts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-success">{stats.completed_attempts}</div>
              <div className="text-sm text-foreground-muted">Completed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground-muted">{stats.abandoned_attempts}</div>
              <div className="text-sm text-foreground-muted">Abandoned</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">
                {formatTime(Math.round(stats.average_time_spent))}
              </div>
              <div className="text-sm text-foreground-muted">Avg. Time</div>
            </div>
          </div>
        </div>
      )}

      {/* Session List */}
      {sessions.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Attempt History</h3>
          <div className="space-y-2">
            {sessions.map((session) => (
              <div 
                key={session.id}
                data-attempt={session.attempt_number}
                className={`border rounded-lg p-4 ${
                  session.status === 'completed' ? 'bg-success-light border-success' :
                  session.status === 'in_progress' ? 'bg-primary/10 border-primary-light' :
                  'bg-background border-border-light'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">
                      Attempt {session.attempt_number}
                      {session.status === 'in_progress' && (
                        <span className="ml-2 text-sm text-primary">(In Progress)</span>
                      )}
                      {session.status === 'completed' && (
                        <span className="ml-2 text-sm text-success">✓ Completed</span>
                      )}
                      {session.status === 'abandoned' && (
                        <span className="ml-2 text-sm text-foreground-muted">(Abandoned)</span>
                      )}
                    </div>
                    <div className="text-sm text-foreground-muted mt-1">
                      Started: {formatDate(session.started_at)}
                      {session.completed_at && (
                        <span> • Completed: {formatDate(session.completed_at)}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-foreground-muted">
                      Progress: {Math.round(session.progress_percentage)}%
                    </div>
                    <div className="text-sm text-foreground-muted time-spent">
                      Time: {formatTime(session.time_spent_seconds)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!activeSession && canAttempt && (
        <div className="flex justify-center">
          <button
            onClick={onStartNew}
            className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            {sessions.length > 0 ? 'Take Again' : 'Start Survey'}
          </button>
        </div>
      )}

      {!canAttempt && (
        <div className="bg-warning-light border border-warning rounded-lg p-4">
          <p className="text-warning">
            Maximum attempts reached. You cannot take this survey again.
          </p>
        </div>
      )}
    </div>
  );
};